import { clearCsrfToken, CSRF_HEADER, currentCsrfToken, setCsrfToken } from './csrf'
import { parseAiStream } from './aiStream'
import type {
  AiAuthorizeResponse,
  AiCredentialStatus,
  AiModelsResponse,
  AiProvider,
  BillingSummary,
  CheckoutResponse,
  CountEnvelope,
  DataEnvelope,
  Invitation,
  InvitationAcceptResponse,
  InvitationPreview,
  MemberListResponse,
  Membership,
  PlansResponse,
  PortalResponse,
  SessionResponse,
  Workspace,
} from './types'
import type {
  ApiErrorBody,
  AuthResponse,
  Note,
  Paginated,
  Project,
  PublicUser,
  Role,
  Sprint,
  Task,
  Timestamp,
  User,
  UUID,
} from '@/types/api'

/**
 * Base URL of the Go API.
 *
 * Left empty in development so requests go to a relative `/api/...` and Vite's
 * proxy forwards them to localhost:8080 — the browser stays same-origin, which
 * is how it behaves in production where the SPA is served from
 * tasklypro.app/app/ alongside the API. Set VITE_API_URL only when pointing the
 * dev server at a different host.
 */
export const API_BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

/** Where the dev API lives when it is not proxied. */
export const DEFAULT_API_URL = 'http://localhost:8080'

/** Laravel's "Page Expired": the CSRF token did not match. */
export const STATUS_CSRF_MISMATCH = 419

/**
 * A non-2xx response from the API.
 *
 * The server always answers with JSON, so `message` is populated for every
 * failure the backend generates. `errors` is present only on a 422.
 */
export class ApiError extends Error {
  readonly status: number
  readonly errors: Record<string, string[]>
  readonly requestId: string | null
  /** Machine-readable code, present on 402 upgrade prompts and a few 409s. */
  readonly code: string | null
  /** Seconds to wait, from `Retry-After`. Present on the auth throttle's 429. */
  readonly retryAfter: number | null

  constructor(
    status: number,
    body: ApiErrorBody,
    requestId: string | null,
    retryAfter: number | null = null,
  ) {
    super(body.message || `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.errors = body.errors ?? {}
    this.requestId = requestId
    this.retryAfter = retryAfter
    // 402 upgrade prompts and the Apple double-billing 409 carry a machine
    // code alongside the human message.
    const code = (body as ApiErrorBody & { code?: unknown }).code
    this.code = typeof code === 'string' ? code : null
  }

  /** The token is missing, expired or revoked — send the user back to /login. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** A validation or domain failure whose `message` is safe to show verbatim. */
  get isValidation(): boolean {
    return this.status === 422
  }

  /** A plan cap was hit; the UI should offer an upgrade. */
  get isUpgradeRequired(): boolean {
    return this.status === 402
  }

  /**
   * The workspace has run out of SEATS specifically.
   *
   * Distinct from isUpgradeRequired because the recovery is different: a
   * content quota wants a toast, but a full workspace wants the invite form
   * replaced by an upgrade prompt.
   */
  get isPlanLimit(): boolean {
    return this.code === 'plan_limit_reached'
  }

  /** The caller is authenticated but lacks the role for this action. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** Billing is not configured on this deployment. */
  get isUnavailable(): boolean {
    return this.status === 503
  }

  /** The throttle rejected this attempt; `retryAfter` says for how long. */
  get isRateLimited(): boolean {
    return this.status === 429
  }

  /** First message for a field, for inline form errors. */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0]
  }
}

/** Thrown when the request never reached the server. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the Taskly API.')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

const UNSAFE_METHODS: ReadonlySet<Method> = new Set<Method>(['POST', 'PUT', 'PATCH', 'DELETE'])

export interface RequestOptions {
  /** Query parameters; null, undefined and empty values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
  /** Bearer token. Omit to rely on the session cookie. */
  token?: string
  headers?: Record<string, string>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

/**
 * Reads `Retry-After` as whole seconds.
 *
 * The header is either a count of seconds or an HTTP date; the auth throttle
 * sends seconds, but a proxy in front of it may rewrite it as a date, and a
 * countdown shown to a person must not depend on which.
 */
function parseRetryAfter(header: string | null): number | null {
  if (!header) return null
  const seconds = Number(header)
  if (Number.isFinite(seconds)) return Math.max(0, Math.round(seconds))
  const date = Date.parse(header)
  if (Number.isNaN(date)) return null
  return Math.max(0, Math.round((date - Date.now()) / 1000))
}

/**
 * Re-reads the session purely to pick up a fresh CSRF token.
 *
 * A 419 is not a sign-out: it means the tab's token is stale (the session slid,
 * or another tab signed in). Recovering silently once is far better UX than
 * bouncing someone to /login mid-form.
 */
async function refreshCsrfToken(signal?: AbortSignal): Promise<boolean> {
  try {
    const session = await request<SessionResponse>(
      'GET',
      '/api/v2/auth/session',
      undefined,
      { signal },
      true,
    )
    setCsrfToken(session.csrf_token)
    return Boolean(session.csrf_token)
  } catch {
    return false
  }
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  const headers: Record<string, string> = {
    // Without this the legacy backend returned an HTML redirect instead of a
    // 401. Always ask for JSON.
    Accept: 'application/json',
    ...options.headers,
  }
  // FormData sets its own Content-Type, and it MUST: the header carries the
  // multipart boundary the browser generates, so writing "multipart/form-data"
  // by hand omits the boundary and the server cannot parse the request at all.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  if (body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json'
  }
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`
  }
  // Cookie-authenticated writes must carry the double-submit token. A bearer
  // caller is exempt server-side, and sending it anyway is harmless.
  if (UNSAFE_METHODS.has(method) && !headers[CSRF_HEADER]) {
    const csrf = currentCsrfToken()
    if (csrf) headers[CSRF_HEADER] = csrf
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      // Send the HttpOnly session cookie the SPA authenticates with.
      credentials: 'include',
      // FormData goes through untouched; JSON.stringify would turn it into the
      // string "[object FormData]" and the upload would silently be nothing.
      body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
      signal: options.signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(cause)
  }

  const requestId = response.headers.get('X-Request-Id')
  const retryAfter = parseRetryAfter(response.headers.get('Retry-After'))

  // One silent recovery from a stale CSRF token, then give up and surface it.
  if (response.status === STATUS_CSRF_MISMATCH && !isRetry && UNSAFE_METHODS.has(method)) {
    if (await refreshCsrfToken(options.signal)) {
      return request<T>(method, path, body, options, true)
    }
  }

  if (response.ok && (response.status === 204 || response.headers.get('Content-Length') === '0')) {
    return undefined as T
  }

  let text: string
  try { text = await response.text() } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(cause)
  }
  if (response.headers.get('Content-Type')?.includes('text/event-stream')) {
    let result
    try { result = parseAiStream(text) } catch {
      throw new ApiError(502, { message: 'The AI connection ended before the drafts were ready. Please try again.', code: 'ai_unavailable' } as ApiErrorBody, requestId)
    }
    if (result.status >= 400) {
      throw new ApiError(result.status, result.payload as ApiErrorBody, requestId, parseRetryAfter(result.retry_after ?? null))
    }
    return result.payload as T
  }
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      // The API contract is JSON-only, so a non-JSON body means something in
      // FRONT of it answered — the reverse proxy, not the API. That happens
      // while the API is restarting, when a request is still in flight as the
      // old process stops accepting: Apache answers with its own bodiless
      // error page and there is nothing to parse.
      //
      // The status is still known, and for those it is worth saying what to
      // do. "The server returned an unreadable response" is true and useless;
      // "it was restarting, try again" is the same fact, acted on.
      const unreachable =
        response.status === 404 ||
        response.status === 502 ||
        response.status === 503 ||
        response.status === 504
      throw new ApiError(
        response.status,
        {
          message: options.headers?.Accept === 'text/event-stream'
            ? 'The AI server could not finish this request. Try fewer issues or a shorter brief.'
            : unreachable
            ? 'The server was briefly unavailable — this usually happens during a deploy. Try again.'
            : 'The server returned an unreadable response.',
        },
        requestId,
        retryAfter,
      )
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, (payload ?? {}) as ApiErrorBody, requestId, retryAfter)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
}

/** Percent-encodes a path segment. Slugs and UUIDs are safe, but ids from a URL are not. */
function seg(value: string): string {
  return encodeURIComponent(value)
}

/* ---------------------------------------------------------------------------
 * v1 endpoint helpers
 *
 * Typed wrappers over the frozen contract. Note the shape difference the
 * backend guarantees: projects and project sprints come back as bare arrays,
 * tasks and notes as paginate envelopes.
 * ------------------------------------------------------------------------ */

export const authApi = {
  register: (input: {
    name: string
    email: string
    password: string
    password_confirmation: string
  }) => api.post<AuthResponse>('/api/auth/register', input),

  login: (input: { email: string; password: string }) =>
    api.post<AuthResponse>('/api/auth/login', input),

  logout: () => api.post<{ message: string }>('/api/auth/logout'),

  me: (options?: RequestOptions) => api.get<User>('/api/me', options),
}

export const projectsApi = {
  list: (options?: RequestOptions) => api.get<Project[]>('/api/projects', options),
  get: (id: UUID) => api.get<Project>(`/api/projects/${seg(id)}`),
  create: (input: {
    name: string
    description?: string
    icon?: string
    color_name?: string
  }) => api.post<Project>('/api/projects', input),
  update: (id: UUID, input: Partial<Pick<Project, 'name' | 'description' | 'icon' | 'color_name'>>) =>
    api.put<Project>(`/api/projects/${seg(id)}`, input),
  remove: (id: UUID) => api.delete<{ message: string }>(`/api/projects/${seg(id)}`),
  sprints: (projectId: UUID) => api.get<Sprint[]>(`/api/projects/${seg(projectId)}/sprints`),
}

export const tasksApi = {
  list: (
    query?: {
      project_id?: UUID
      sprint_id?: UUID
      status?: string
      priority?: string
      assigned_to?: string
      search?: string
      per_page?: number
      page?: number
    },
    options?: RequestOptions,
  ) => api.get<Paginated<Task>>('/api/tasks', { ...options, query }),

  get: (id: UUID) => api.get<Task>(`/api/tasks/${seg(id)}`),

  create: (input: {
    project_id: UUID
    sprint_id?: UUID | null
    title: string
    description?: string
    status?: Task['status']
    priority?: Task['priority']
    tags?: string[]
  }) => api.post<Task>('/api/tasks', input),

  update: (id: UUID, input: Partial<Task>) => api.put<Task>(`/api/tasks/${seg(id)}`, input),
  remove: (id: UUID) => api.delete<{ message: string }>(`/api/tasks/${seg(id)}`),
  complete: (id: UUID, workLog?: string) =>
    api.post<Task>(`/api/tasks/${seg(id)}/complete`, workLog ? { work_log: workLog } : {}),
  addNote: (id: UUID, note: string) => api.post<Task>(`/api/tasks/${seg(id)}/note`, { note }),
}

export const notesApi = {
  list: (query?: { project_id?: UUID; search?: string; per_page?: number; page?: number }) =>
    api.get<Paginated<Note>>('/api/notes', { query }),
  get: (id: UUID) => api.get<Note>(`/api/notes/${seg(id)}`),
}

export interface HealthResponse {
  status: 'ok' | 'degraded'
  version: string
  env: string
  dialect: string
  database: string
  time: string
}

export const healthApi = {
  check: (options?: RequestOptions) => api.get<HealthResponse>('/api/healthz', options),
}

/* ---------------------------------------------------------------------------
 * v2 session
 *
 * The web app authenticates with an HttpOnly cookie, not a bearer token: no
 * credential is ever held in JavaScript, so an XSS cannot exfiltrate one. Every
 * response that establishes or refreshes a session carries the CSRF token,
 * which is captured here so callers never have to remember to.
 * ------------------------------------------------------------------------ */

function captureSession(session: SessionResponse): SessionResponse {
  setCsrfToken(session.csrf_token)
  return session
}

export const sessionApi = {
  register: async (input: {
    name: string
    email: string
    password: string
    password_confirmation: string
  }) => captureSession(await api.post<SessionResponse>('/api/v2/auth/register', input)),

  /**
   * Signs in with the cookie session.
   *
   * `remember` is the "Keep me signed in" answer and is a real parameter, not a
   * hint: false shortens the server-side session and makes both cookies
   * browser-lifetime, so closing the browser ends the session. Omitting it
   * keeps the full SESSION_TTL, which is what every caller predating the field
   * still gets.
   */
  login: async (input: { email: string; password: string; remember?: boolean }) =>
    captureSession(await api.post<SessionResponse>('/api/v2/auth/login', input)),

  current: async (options?: RequestOptions) =>
    captureSession(await api.get<SessionResponse>('/api/v2/auth/session', options)),

  logout: async () => {
    try {
      await api.post<{ message: string }>('/api/v2/auth/logout')
    } finally {
      // Whatever the server said, this tab must stop replaying the old token.
      clearCsrfToken()
    }
  },

  /** Re-points the session at another workspace by id. */
  setOrganization: async (organizationId: UUID) =>
    captureSession(
      await api.post<SessionResponse>('/api/v2/auth/session/organization', {
        organization_id: organizationId,
      }),
    ),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/api/v2/auth/password/forgot', { email }),

  resetPassword: (input: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }) => api.post<{ message: string }>('/api/v2/auth/password/reset', input),

  changePassword: (input: {
    current_password: string
    password: string
    password_confirmation: string
  }) => api.post<{ message: string }>('/api/v2/auth/password', input),

  verifyEmail: (token: string) => api.post<{ message: string }>('/api/v2/auth/email/verify', { token }),

  resendVerification: () => api.post<{ message: string }>('/api/v2/auth/email/resend'),
}

/* ---------------------------------------------------------------------------
 * v2 workspaces
 * ------------------------------------------------------------------------ */

const orgPath = (slug: string) => `/api/v2/orgs/${seg(slug)}`

export const orgsApi = {
  list: (options?: RequestOptions) =>
    api.get<CountEnvelope<Workspace>>('/api/v2/orgs', options).then((r) => r.data),

  get: (slug: string, options?: RequestOptions) =>
    api.get<DataEnvelope<Workspace>>(orgPath(slug), options).then((r) => r.data),

  create: (input: { name: string; slug?: string }) =>
    api.post<DataEnvelope<Workspace>>('/api/v2/orgs', input).then((r) => r.data),

  update: (slug: string, input: { name?: string; slug?: string }) =>
    api.patch<DataEnvelope<Workspace>>(orgPath(slug), input).then((r) => r.data),

  remove: (slug: string) => api.delete<void>(orgPath(slug)),

  /** Makes this the caller's active workspace across sessions and devices. */
  switchTo: (slug: string) =>
    api.post<DataEnvelope<Workspace>>(`${orgPath(slug)}/switch`).then((r) => r.data),

  transferOwnership: (slug: string, userId: number) =>
    api
      .post<DataEnvelope<Workspace>>(`${orgPath(slug)}/transfer-ownership`, { user_id: userId })
      .then((r) => r.data),
}

/* ---------------------------------------------------------------------------
 * v2 projects
 *
 * The org-scoped project surface. It differs from `projectsApi` above in the
 * one way that matters: the workspace comes from the path and is resolved
 * server-side against the caller's own membership, so the client never filters
 * another tenant's rows out of a wider list — they are never in the response.
 *
 * A project is addressed by its KEY ("TSK"), a retired key, or its UUID, which
 * is the same resolver the board, backlog and sprint routes use.
 * ------------------------------------------------------------------------ */

/** A project as the v2 surface returns it: the v1 shape plus board metadata. */
export interface OrgProject extends Project {
  /** Issues not in the `done` status. */
  open_issue_count: number
  lead_user_id?: number | null
  archived_at?: Timestamp | null
  issue_seq?: number
  lead?: PublicUser
}

export interface CreateProjectInput {
  name: string
  /** 2–10 letters or digits starting with a letter. Derived from the name when omitted. */
  key?: string
  description?: string
  icon?: string
  color?: string
  lead_user_id?: number | null
}

export const orgProjectsApi = {
  list: (slug: string, options?: RequestOptions & { includeArchived?: boolean }) =>
    api
      .get<CountEnvelope<OrgProject>>(`${orgPath(slug)}/projects`, {
        ...options,
        query: { include_archived: options?.includeArchived ? 'true' : undefined },
      })
      .then((r) => r.data),

  get: (slug: string, ref: string, options?: RequestOptions) =>
    api.get<OrgProject>(`${orgPath(slug)}/projects/${seg(ref)}`, options),

  create: (slug: string, input: CreateProjectInput) =>
    api.post<OrgProject>(`${orgPath(slug)}/projects`, input),

  update: (
    slug: string,
    ref: string,
    input: Partial<CreateProjectInput> & { archived?: boolean; is_default?: boolean },
  ) => api.patch<OrgProject>(`${orgPath(slug)}/projects/${seg(ref)}`, input),
}

/* ---------------------------------------------------------------------------
 * Claude connection
 *
 * Anthropic's consent page redirects to its OWN callback, which displays a
 * code for the user to copy — there is no redirect back to Taskly. So the flow
 * is: ask the server for a URL, open it in a new tab, paste the code back.
 *
 * That URL is credential-bearing: `state` IS the PKCE verifier in this flow
 * (Anthropic's design), so the authorize URL must only ever be handed to
 * window.open — never logged, never persisted, never put in a query string of
 * our own. No token reaches the browser at any point.
 *
 * All four endpoints are admin only, the status read included.
 * ------------------------------------------------------------------------ */

export const aiCredentialsApi = {
  status: (slug: string, options?: RequestOptions) =>
    api.get<AiCredentialStatus>(`${orgPath(slug)}/ai`, options),

  /** Step one. Returns the URL to open — see above: it carries the verifier. */
  authorize: (slug: string, provider: AiProvider = 'anthropic') =>
    api.post<AiAuthorizeResponse>(`${orgPath(slug)}/ai/authorize`, { provider }),

  pollChatGPT: (slug: string, requestId: string, signal?: AbortSignal) =>
    api.post<{ pending: boolean; status?: AiCredentialStatus }>(`${orgPath(slug)}/ai/chatgpt/poll`, { request_id: requestId }, { signal }),

  cancelChatGPT: (slug: string, requestId: string) =>
    api.post(`${orgPath(slug)}/ai/chatgpt/cancel`, { request_id: requestId }),

  models: (slug: string, signal?: AbortSignal) =>
    api.get<AiModelsResponse>(`${orgPath(slug)}/ai/models`, { signal }),

  selectModel: (slug: string, model: string) =>
    api.put<AiCredentialStatus>(`${orgPath(slug)}/ai/model`, { model }),

  /** Step two: the code Claude displayed, pasted back. Returns status only. */
  connect: (slug: string, code: string) =>
    api.post<AiCredentialStatus>(`${orgPath(slug)}/ai/connect`, { code }),

  disconnect: (slug: string) => api.delete<AiCredentialStatus>(`${orgPath(slug)}/ai`),
}

export const membersApi = {
  list: (slug: string, options?: RequestOptions) =>
    api.get<MemberListResponse>(`${orgPath(slug)}/members`, options),

  changeRole: (slug: string, userId: number, role: Role) =>
    api
      .patch<DataEnvelope<Membership>>(`${orgPath(slug)}/members/${userId}`, { role })
      .then((r) => r.data),

  remove: (slug: string, userId: number) => api.delete<void>(`${orgPath(slug)}/members/${userId}`),
}

export const invitationsApi = {
  list: (slug: string, options?: RequestOptions) =>
    api.get<CountEnvelope<Invitation>>(`${orgPath(slug)}/invitations`, options).then((r) => r.data),

  create: (slug: string, input: { email: string; role: Role }) =>
    api.post<DataEnvelope<Invitation>>(`${orgPath(slug)}/invitations`, input).then((r) => r.data),

  resend: (slug: string, id: UUID) =>
    api
      .post<DataEnvelope<Invitation>>(`${orgPath(slug)}/invitations/${seg(id)}/resend`)
      .then((r) => r.data),

  revoke: (slug: string, id: UUID) => api.delete<void>(`${orgPath(slug)}/invitations/${seg(id)}`),

  /** Invitations addressed to the signed-in user. Carries no raw tokens. */
  inbox: (options?: RequestOptions) =>
    api.get<CountEnvelope<InvitationPreview>>('/api/v2/invitations', options).then((r) => r.data),

  /** Public preview by raw token — the only unauthenticated tenancy route. */
  preview: (token: string, options?: RequestOptions) =>
    api
      .get<DataEnvelope<InvitationPreview>>(`/api/v2/invitations/${seg(token)}`, options)
      .then((r) => r.data),

  /** Accepts by raw token (from the email) or by invitation id (from the inbox). */
  accept: (tokenOrId: string) =>
    api
      .post<DataEnvelope<InvitationAcceptResponse>>(`/api/v2/invitations/${seg(tokenOrId)}/accept`)
      .then((r) => r.data),

  decline: (tokenOrId: string) =>
    api.post<unknown>(`/api/v2/invitations/${seg(tokenOrId)}/decline`),
}

/* ---------------------------------------------------------------------------
 * Billing
 *
 * `plans` is public — it is the pricing page. Everything else is scoped to one
 * workspace by slug and additionally checked against the caller's membership
 * server-side; checkout and portal are owner-only.
 * ------------------------------------------------------------------------ */

const billingPath = (slug: string) => `/api/billing/orgs/${seg(slug)}`

export const billingApi = {
  plans: (options?: RequestOptions) => api.get<PlansResponse>('/api/billing/plans', options),

  summary: (slug: string, options?: RequestOptions) =>
    api.get<BillingSummary>(`${billingPath(slug)}/summary`, options),

  checkout: (slug: string, input: { plan: string; interval: 'month' | 'year' }) =>
    api.post<CheckoutResponse>(`${billingPath(slug)}/checkout`, input),

  portal: (slug: string) => api.post<PortalResponse>(`${billingPath(slug)}/portal`),

  syncSeats: (slug: string) =>
    api.post<{ seats: number }>(`${billingPath(slug)}/seats/sync`),
}
